/**
 * Discover, from hung-hola blocks/discover/view.js:
 * - The "Ask Hola anything…" box is a GET form to the chat page, in a new tab (render.php, client round 1 #4). An empty
 *   prompt is left out of the URL, so the chat opens without one.
 * - The whole chat box acts as the input (owner, 2026-10-19): a click anywhere in it that is not a link or a button
 *   focuses the input. Links to the block's anchor (hero "Scroll to meet Hola") focus it too, once the page has
 *   stopped scrolling and the shared fade-in has shown the chat box (a hidden input cannot take focus).
 * - The photo strip loops sideways (endless, like the partners strip, GSAP utils.wrap). The strip is printed twice;
 *   copy k sits at k * setWidth (strip + GAP) from the first. The shared offset wraps inside [-left - setWidth, -left),
 *   so the strip always covers the section's left edge. Right to left on desktop, the strip first moves into the
 *   empty space Figma leaves on its left and only wraps once it covers the left edge, so the first frame is the still
 *   design. Direction from the "direction" field on [data-discover-direction]: -1 right to left, 1 left to right.
 * - Speed: always SPEED px/s; neither the mouse nor the page scroll changes it (owner 2026-10-20, so not on the
 *   shared loopMotion()). Pauses off screen.
 *   ?still and reduced motion keep the still strip (motionLib() is null).
 */
import { motionLib } from '../../assets/js/motion';

const SPEED = 40; // px per second (owner: 20 → 40)
const GAP = 187; // px between two copies of the strip: Figma gap between "Office" and the photo right of it
const SCROLL_FALLBACK = 1500; // ms, focus anyway when no scrollend comes (already there, or no support)
const SHOW_TIMEOUT = 3000; // ms, stop waiting for the fade-in to show the chat box

function initChat(wrapper: HTMLElement): void {
	const chat = wrapper.querySelector<HTMLFormElement>('[data-discover-chat]');
	const prompt = wrapper.querySelector<HTMLInputElement>('[data-discover-prompt]');
	if (!chat || !prompt) {
		return;
	}
	chat.addEventListener('submit', () => {
		// A disabled input is not sent; back on the next frame, once the form has been read
		if (!prompt.value.trim()) {
			prompt.disabled = true;
			requestAnimationFrame(() => {
				prompt.disabled = false;
			});
		}
	});
	chat.addEventListener('click', (e) => {
		if (!(e.target as Element).closest('a, button, input')) {
			prompt.focus();
		}
	});

	const focusWhenShown = (since = performance.now()): void => {
		const style = getComputedStyle(chat);
		const shown = style.visibility === 'visible' && style.opacity === '1';
		if (shown || performance.now() - since > SHOW_TIMEOUT) {
			prompt.focus({ preventScroll: true });
			return;
		}
		requestAnimationFrame(() => focusWhenShown(since));
	};

	if (!wrapper.id) {
		return;
	}
	const id = CSS.escape(wrapper.id);
	document.querySelectorAll(`a[href="#${id}"], a[href$="/#${id}"]`).forEach((link) => {
		link.addEventListener('click', () => {
			let done = false;
			const settle = (): void => {
				if (done) {
					return;
				}
				done = true;
				focusWhenShown();
			};
			window.addEventListener('scrollend', settle, { once: true });
			window.setTimeout(settle, SCROLL_FALLBACK);
		});
	});
}

function initStrip(wrapper: HTMLElement): void {
	const lists = [...wrapper.querySelectorAll<HTMLElement>('[data-discover-gallery]')];
	const lib = motionLib();
	if (!lists.length || !lib) {
		return;
	}
	const { gsap } = lib;
	const direction = wrapper.querySelector<HTMLElement>('[data-discover-direction]')?.dataset.discoverDirection === '1' ? 1 : -1;
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	let tween: gsap.core.Tween | null = null;
	let inView = false;
	let looped = false;

	const stop = (): void => {
		tween?.kill();
		tween = null;
		gsap.set(lists, { clearProps: 'transform' });
		lists.slice(1).forEach((list) => (list.hidden = true));
	};

	const build = (): void => {
		const progress = tween ? tween.progress() : 0;
		stop();
		if (reduceMotion.matches) {
			return;
		}
		const width = lists[0].offsetWidth;
		const setWidth = width + GAP;
		if (!width) {
			return;
		}

		// At the worst point of the loop the last copy must still reach the section's right edge
		while ((lists.length - 2) * setWidth + width < wrapper.clientWidth) {
			// From the printed copy: the first strip's photos carry the fade-in and may still be hidden inline
			const copy = (lists[1] ?? lists[0]).cloneNode(true) as HTMLElement;
			copy.setAttribute('aria-hidden', 'true');
			copy.removeAttribute('data-px-fade-in');
			lists[lists.length - 1].after(copy);
			lists.push(copy);
		}
		lists.forEach((list) => (list.hidden = false));

		const left = lists[0].getBoundingClientRect().left - wrapper.getBoundingClientRect().left;
		const wrapX = gsap.utils.wrap(-left - setWidth, -left);
		const place = (x: number): number => {
			if (!looped && direction < 0 && x > -left) {
				return x;
			}
			looped = true;
			return wrapX(x);
		};
		const offset = (target: HTMLElement): number => lists.indexOf(target) * setWidth;
		const start = direction * progress * setWidth;
		gsap.set(lists, { x: looped ? wrapX(start) : start });
		tween = gsap.to(lists, {
			x: `${direction > 0 ? '+=' : '-='}${setWidth}`,
			duration: setWidth / SPEED,
			ease: 'none',
			repeat: -1,
			modifiers: { x: (x: string, target: HTMLElement) => `${place(parseFloat(x)) + offset(target)}px` },
			paused: !inView,
		});
	};

	// Rebuild when the section changes size (breakpoint: the strip moves and changes height)
	let frame = 0;
	new ResizeObserver(() => {
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(build);
	}).observe(wrapper);

	new IntersectionObserver(([entry]) => {
		inView = entry.isIntersecting;
		if (tween) {
			if (inView) {
				tween.play();
			} else {
				tween.pause();
			}
		}
	}).observe(wrapper);

	reduceMotion.addEventListener('change', build);
}

document.querySelectorAll<HTMLElement>('[data-px-block="discover"]').forEach((wrapper) => {
	initChat(wrapper);
	initStrip(wrapper);
});
